// src/belediye/pages/UnitSelectionPage.js
import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import "./UnitSelectionPage.css";

import api from "../services/belediyeApi";

/* Birim kartı ikonu (inline SVG — ek bağımlılık yok) */
const BuildingIcon = (props) => (
  <svg
    viewBox="0 0 24 24"
    width="22"
    height="22"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.7"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    {...props}
  >
    <path d="M3 21h18" />
    <path d="M6 21V5a1 1 0 0 1 1-1h8a1 1 0 0 1 1 1v16" />
    <path d="M16 21V9h3a1 1 0 0 1 1 1v11" />
    <path d="M9.5 8h.01M12.5 8h.01M9.5 12h.01M12.5 12h.01M9.5 16h3" />
  </svg>
);

const LS = {
  accessToken: "accessToken",
  refreshToken: "refreshToken",
  user: "user",
  activeUnitId: "activeUnitId",
  activeUnitSlug: "activeUnitSlug",
  activeEffectiveRole: "activeEffectiveRole",
};

/* ----------------------------- Helpers ----------------------------- */

function safeJsonParse(value) {
  try {
    return value ? JSON.parse(value) : null;
  } catch {
    return null;
  }
}

function readStoredUser() {
  return safeJsonParse(localStorage.getItem(LS.user));
}

function writeStoredUser(nextUser) {
  localStorage.setItem(LS.user, JSON.stringify(nextUser));
}

function normalizeRoleCode(roleCode) {
  return String(roleCode || "")
    .trim()
    .toLowerCase();
}

function isSahaRole(roleCode) {
  return normalizeRoleCode(roleCode) === "saha_personeli";
}

/**
 * Rol koduna göre kart RENK sınıfı.
 * NOT: Rol METNİ kullanıcıya gösterilmez (dahili bilgi). Yalnız renk sinyali
 * verilir; kullanıcı farklı birimlerde farklı rollerdeyse kartlar farklı
 * renklerde gelir.
 */
function roleAccentClass(roleCode) {
  const code = normalizeRoleCode(roleCode);
  if (!code) return "unit-card--norole";
  if (code === "saha_personeli") return "unit-card--saha";
  if (code === "birim_personeli") return "unit-card--birim";
  if (
    code === "personel_1" ||
    code === "mudur" ||
    code === "müdür" ||
    code === "baskan_yardimcisi" ||
    code === "baskan_yardimcisi_all"
  ) {
    return "unit-card--yonetici";
  }
  if (code === "personel_2" || code === "halk_masasi") return "unit-card--halk";
  if (code === "başkan" || code === "baskan") return "unit-card--baskan";
  return "unit-card--other";
}

/**
 * Unit verisini tek forma sokalım (UI katmanı için).
 * Backend my-units formatı:
 * { unit_id, unit_name, unit_slug, is_active, role, effective_role }
 */
function normalizeUnit(raw) {
  if (!raw) return null;

  const unit_id = raw.unit_id ?? raw.id ?? raw.unit ?? null;
  const unit_slug = raw.unit_slug ?? raw.slug ?? null;
  const unit_name = raw.unit_name ?? raw.name ?? null;

  const role = raw.role ?? null;
  const effective_role = raw.effective_role ?? role ?? null;

  const is_active = raw.is_active !== false;

  return {
    unit_id: unit_id ? Number(unit_id) : null,
    unit_slug: unit_slug ? String(unit_slug) : "",
    unit_name: unit_name ? String(unit_name) : "",
    role: role ? String(role) : "",
    effective_role: effective_role ? String(effective_role) : "",
    is_active,
  };
}

/* ----------------------------- Component ----------------------------- */

export default function UnitSelectionPage({ units: unitsProp = [] }) {
  const navigate = useNavigate();
  const location = useLocation();

  const [units, setUnits] = useState(() =>
    Array.isArray(unitsProp) ? unitsProp.map(normalizeUnit).filter(Boolean) : []
  );

  const [loading, setLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState("");

  // Not: storage değişirse bu değer otomatik güncellenmez; bu sayfa için yeterli.
  const user = useMemo(() => readStoredUser(), []);
  const displayName = useMemo(() => {
    const first = user?.first_name ? String(user.first_name).trim() : "";
    const last = user?.last_name ? String(user.last_name).trim() : "";
    const full = `${first} ${last}`.trim();
    return full || (user?.username ? String(user.username) : "Kullanıcı");
  }, [user]);

  /**
   * Birimler:
   * 1) props.units gelmişse onu kullan
   * 2) localStorage user.units varsa onu kullan
   * 3) /common/users/my-units/ dene
   * 4) legacy fallback: user.unit varsa /common/units/{id}/ ile tek birim üret
   */
  useEffect(() => {
    let mounted = true;

    const bootstrap = async () => {
      setLoading(true);
      setError("");

      // 1) props üzerinden
      if (Array.isArray(unitsProp) && unitsProp.length) {
        if (mounted) {
          setUnits(unitsProp.map(normalizeUnit).filter(Boolean));
          setLoading(false);
        }
        return;
      }

      const storedUser = readStoredUser();

      // 2) localStorage user.units
      const storedUnitsRaw = Array.isArray(storedUser?.units) ? storedUser.units : [];
      if (storedUnitsRaw.length) {
        if (mounted) {
          setUnits(storedUnitsRaw.map(normalizeUnit).filter(Boolean));
          setLoading(false);
        }
        return;
      }

      // 3) API: my-units
      const token = localStorage.getItem(LS.accessToken);
      if (!token) {
        if (mounted) {
          setError("Oturum bulunamadı. Lütfen tekrar giriş yapın.");
          setLoading(false);
        }
        return;
      }

      try {
        const res = await api.get("/common/users/my-units/");
        const apiUnitsRaw = Array.isArray(res?.data?.units) ? res.data.units : [];
        const apiUnits = apiUnitsRaw.map(normalizeUnit).filter(Boolean);

        if (apiUnits.length) {
          if (mounted) {
            setUnits(apiUnits);

            // user objesine units ekleyelim (bir daha çağırmayalım)
            if (storedUser) {
              writeStoredUser({ ...storedUser, units: apiUnitsRaw });
            }

            setLoading(false);
          }
          return;
        }
      } catch {
        // devam: legacy fallback
      }

      // 4) Legacy fallback: user.unit tek birim
      try {
        const legacyUnitId = storedUser?.unit;
        if (legacyUnitId && typeof legacyUnitId === "number") {
          const unitRes = await api.get(`/common/units/${legacyUnitId}/`);
          const synthetic = normalizeUnit({
            unit_id: legacyUnitId,
            unit_slug: unitRes?.data?.slug,
            unit_name: unitRes?.data?.name,
            is_active: true,
            role: storedUser?.role,
            effective_role: storedUser?.role,
          });

          if (synthetic?.unit_id && synthetic?.unit_slug) {
            if (mounted) {
              setUnits([synthetic]);
              setLoading(false);
            }
            return;
          }
        }

        if (mounted) {
          setError("Bu kullanıcıya ait birim bulunamadı.");
          setLoading(false);
        }
      } catch {
        if (mounted) {
          setError("Birim listesi alınamadı. Lütfen tekrar deneyin.");
          setLoading(false);
        }
      }
    };

    bootstrap();

    return () => {
      mounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unitsProp]);

  const sortedUnits = useMemo(() => {
    const list = Array.isArray(units) ? [...units] : [];
    list.sort((a, b) =>
      String(a?.unit_name || "").localeCompare(String(b?.unit_name || ""), "tr")
    );
    return list;
  }, [units]);

  /**
   * Logout: sadece auth + seçili birim key’lerini temizle
   */
  const logout = () => {
    localStorage.removeItem(LS.accessToken);
    localStorage.removeItem(LS.refreshToken);
    localStorage.removeItem(LS.user);

    localStorage.removeItem(LS.activeUnitId);
    localStorage.removeItem(LS.activeUnitSlug);
    localStorage.removeItem(LS.activeEffectiveRole);

    navigate("/giris", { replace: true });
  };

  /**
   * Seçim akışı:
   * - validate-unit POST
   * - LS.activeUnit* set
   * - stored user objesini backward-compat için güncelle (user.unit, user.role)
   * - yönlendir
   *
   * replace:false => dashboard’dan geri gelince birim seç ekranı geri gelir
   */
  const onSelectUnit = async (unit) => {
    if (!unit?.unit_id || !unit?.unit_slug) return;
    if (unit.is_active === false) return;

    const rawEffective = unit.effective_role || unit.role || "";
    if (!rawEffective) {
      setError("Bu birimde kullanıcı yetkisi tanımlı değil. Lütfen yöneticinize başvurun.");
      return;
    }

    setError("");
    setIsSubmitting(true);

    try {
      const payload = {
        unit_id: unit.unit_id,
        unit_slug: unit.unit_slug,
        unit: unit.unit_id, // legacy uyumluluk
      };

      const res = await api.post("/common/users/validate-unit/", payload, {
        headers: {
          "X-Unit-Id": String(unit.unit_id),
          "X-Unit-Slug": unit.unit_slug,
        },
      });

      const effectiveRole =
        res?.data?.effective_role || unit.effective_role || unit.role || "";

      // aktif unit cache
      localStorage.setItem(LS.activeUnitId, String(unit.unit_id));
      localStorage.setItem(LS.activeUnitSlug, unit.unit_slug);
      localStorage.setItem(LS.activeEffectiveRole, effectiveRole);

      // backward compat: mevcut ekranların bir kısmı user.unit ve user.role okuyor
      const storedUser = readStoredUser();
      if (storedUser) {
        writeStoredUser({
          ...storedUser,
          unit: unit.unit_id,
          role: effectiveRole || storedUser.role,
          active_unit_id: unit.unit_id,
          active_unit_slug: unit.unit_slug,
          active_effective_role: effectiveRole,
        });
      }

      const target =
        normalizeRoleCode(effectiveRole) === "saha_personeli"
          ? `/yonetim/${unit.unit_slug}/saha`
          : `/yonetim/${unit.unit_slug}/dashboard`;

      navigate(target, { replace: false, state: { from: location.pathname } });
    } catch (err) {
      const status = err?.response?.status;
      const code = err?.response?.data?.code;
      const msg =
        err?.response?.data?.error ||
        err?.response?.data?.detail ||
        "Birim doğrulanamadı. Lütfen tekrar deneyin.";

      // 409: membership_role_missing gibi durumlar
      if (status === 409 && code) setError(msg);
      else if (status === 403) setError(msg);
      else setError(msg);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="unit-selection-page">
      <div className="unit-selection-container">
        {/* Header */}
        <div className="unit-selection-header">
          <div className="unit-selection-greeting">
            <div className="unit-selection-hello">Hoş geldiniz</div>
            <div className="unit-selection-name">{displayName}</div>
          </div>

          <button
            type="button"
            className="unit-selection-logout"
            onClick={logout}
            disabled={isSubmitting}
            aria-label="Çıkış Yap"
            title="Çıkış Yap"
          >
            Çıkış Yap
          </button>
        </div>

        <div className="unit-selection-subtitle">
          Lütfen çalışmak istediğiniz birimi seçin.
        </div>

        {error ? (
          <div className="unit-selection-alert" role="alert">
            {error}
          </div>
        ) : null}

        {loading ? (
          <div className="unit-selection-loading">Birimler yükleniyor...</div>
        ) : (
          <div className="unit-card-grid" role="list" aria-label="Birim listesi">
            {sortedUnits.map((unit) => {
              if (!unit?.unit_id) return null;

              const effectiveRole = unit.effective_role || unit.role || "";
              const roleIsSaha = isSahaRole(effectiveRole);
              const accentClass = roleAccentClass(effectiveRole);

              const disabled =
                isSubmitting || unit.is_active === false || !effectiveRole;

              return (
                <div
                  key={unit.unit_id}
                  className={[
                    "unit-card",
                    accentClass,
                    disabled ? "unit-card--disabled" : "",
                  ]
                    .filter(Boolean)
                    .join(" ")}
                  role="listitem"
                >
                  {/* Rol rengi üst şerit — rol METNİ gösterilmez, yalnız renk */}
                  <span className="unit-card-accent" aria-hidden="true" />

                  <div className="unit-card-top">
                    <span className="unit-card-icon" aria-hidden="true">
                      <BuildingIcon />
                    </span>

                    <div className="unit-card-titlewrap">
                      <div className="unit-card-title">{unit.unit_name}</div>
                      {unit.is_active === false ? (
                        <span className="unit-chip unit-chip--inactive">Pasif</span>
                      ) : null}
                    </div>
                  </div>

                  <div className="unit-card-meta">
                    {effectiveRole ? (
                      <span className="unit-card-meta-text">
                        Bu birimle devam etmek için seçin.
                      </span>
                    ) : (
                      <span className="unit-card-meta-text unit-card-meta-text--warn">
                        Bu birimde yetkiniz tanımlı değil.
                      </span>
                    )}
                  </div>

                  <div className="unit-card-actions">
                    <button
                      type="button"
                      className={[
                        "unit-card-cta",
                        roleIsSaha ? "unit-card-cta--saha" : "",
                      ]
                        .filter(Boolean)
                        .join(" ")}
                      onClick={() => onSelectUnit(unit)}
                      disabled={disabled}
                      aria-disabled={disabled}
                    >
                      Bu birimle devam et
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        <div className="unit-selection-footnote">
          Sorun yaşıyorsanız birim yöneticinizle iletişime geçin.
        </div>
      </div>
    </div>
  );
}
